import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { updateCommunityTask } from '../services/api';

const EditCommunityTask = ({ task, onClose, onTaskUpdated }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: task.title || '',
    description: task.description || '',
    type: task.type || 'cryptography',
    content: task.content || '',
    solution: task.solution || '',
    hint: task.hint || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Validate form data
      if (!formData.title.trim()) {
        throw new Error('Title is required');
      }
      if (!formData.description.trim()) {
        throw new Error('Description is required');
      }
      if (!formData.content.trim()) {
        throw new Error('Content is required');
      }
      if (!formData.solution.trim()) {
        throw new Error('Solution is required');
      }
      
      // Check if user has enough WR coins (20)
      if ((user?.wrCoins || 0) < 20) {
        throw new Error(`You need 20 WR Coins to edit a community task. You have ${user?.wrCoins || 0} WR Coins.`);
      }
      
      // Update the task
      const response = await updateCommunityTask(task._id, formData);
      
      // Update local user data with new coin balance
      if (response.remainingCoins !== undefined) {
        // Update user's balance in localStorage
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const userData = JSON.parse(userStr);
          userData.wrCoins = response.remainingCoins;
          localStorage.setItem('user', JSON.stringify(userData));
        }
      }
      
      // Notify parent component and close modal
      onTaskUpdated(response.task);
    } catch (err) {
      console.error('Error updating community task:', err);
      setError(err.message || 'Failed to update task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="community-modal">
      <div className="community-modal-content">
        <button 
          className="community-modal-close" 
          onClick={onClose}
        >
          ✕
        </button>
        
        <h2 className="community-modal-title">Edit Community Task</h2>
        
        {error && (
          <div className="community-result error">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="community-form-group">
            <label className="community-form-label">Title:</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="community-form-input"
              placeholder="Enter a catchy title for your task"
              required
            />
          </div>
          
          <div className="community-form-group">
            <label className="community-form-label">Description:</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="community-form-textarea"
              placeholder="Briefly describe your task"
              required
            />
          </div>
          
          <div className="community-form-group">
            <label className="community-form-label">Type:</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="community-form-select"
              required
            >
              <option value="cryptography">Cryptography</option>
              <option value="coding">Coding</option>
              <option value="investigation">Investigation</option>
              <option value="network">Network</option>
              <option value="website">Website</option>
            </select>
          </div>
          
          
          <div className="community-form-group">
            <label className="community-form-label">Task Content:</label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              className="community-form-textarea"
              placeholder="Enter the full task content/challenge here"
              required
            />
          </div>
          
          <div className="community-form-group">
            <label className="community-form-label">Solution:</label>
            <textarea
              name="solution"
              value={formData.solution}
              onChange={handleChange}
              className="community-form-textarea"
              placeholder="Enter the correct solution/answer to your task"
              required
            />
          </div>
          
          <div className="community-form-group">
            <label className="community-form-label">Hint (Optional):</label>
            <textarea
              name="hint"
              value={formData.hint}
              onChange={handleChange}
              className="community-form-textarea"
              placeholder="Provide a hint to help others solve your task"
            />
          </div>

          
          <div className="community-form-group">
            <p><strong>Edit Cost:</strong> 20 WR Coins</p>
            <p><strong>Your Balance:</strong> {user.wrCoins} WR Coins</p>
          </div>
          
          <div className="community-form-actions">
            <button 
              type="button" 
              className="community-form-cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="community-form-create"
              disabled={loading || user.wrCoins < 20}
            >
              {loading ? 'Updating...' : 'Update Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCommunityTask;